import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { UserListHead, UserListToolbar } from 'src/sections/@dashboard/user';
import hit from 'src/services/manager';
import { setLoader } from 'src/redux/loader';
import { setToast } from 'src/redux/toast';

const TABLE_HEAD = [
  { id: 'code', label: 'Code', alignRight: false },
  { id: 'type', label: 'Type', alignRight: false },
  { id: 'isSingleUse', label: 'Single Use', alignRight: false },
  { id: 'isUsed', label: 'Used', alignRight: false },
  { id: 'createdAt', label: 'Created', alignRight: false },
  { id: '_id', label: 'ID', alignRight: false },
];

const getAccessCodeValue = (row, key) => {
  switch (key) {
    case 'code':
      return row?.code || '';
    case 'type':
      return row?.type || '';
    case 'isSingleUse':
      return String(Boolean(row?.isSingleUse));
    case 'isUsed':
      return String(Boolean(row?.isUsed));
    case 'createdAt':
      return row?.createdAt || row?.created_at || '';
    case '_id':
      return row?._id || row?.id || '';
    default:
      return row?.[key] || '';
  }
};

function descendingComparator(a, b, orderBy) {
  const aValue = getAccessCodeValue(a, orderBy);
  const bValue = getAccessCodeValue(b, orderBy);
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    const q = query.toLowerCase();
    return stabilized
      .map((el) => el[0])
      .filter((row) => (row?.code || '').toLowerCase().includes(q) || (row?.type || '').toLowerCase().includes(q));
  }
  return stabilized.map((el) => el[0]);
}

export default function AccessCodesPage() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [accessCodes, setAccessCodes] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [formData, setFormData] = useState({
    type: 'dtc',
    code: '',
    isSingleUse: true,
  });

  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);
  const writes = useSelector((state) => state.navs)?.write;

  const canCreate =
    user?.role === 'admin' || (user?.role === 'staff' && Array.isArray(writes) && writes.includes('accessCodes'));

  const fetchAccessCodes = async () => {
    try {
      dispatch(setLoader(true));
      const res = await hit('/admin/access-codes', 'get');
      if (res.err) {
        dispatch(setToast({ show: true, message: res.msg || 'Failed to load access codes.', severity: 'error' }));
        return;
      }
      const list =
        res.data?.results || res.data?.data || res.data?.accessCodes || res.data?.codes || res.data || [];
      setAccessCodes(Array.isArray(list) ? list : []);
    } catch (err) {
      dispatch(setToast({ show: true, message: 'Failed to load access codes.', severity: 'error' }));
    } finally {
      dispatch(setLoader(false));
    }
  };

  useEffect(() => {
    fetchAccessCodes();
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const openCreateDialog = () => {
    if (!canCreate) {
      dispatch(setToast({ show: true, message: 'Sorry you are not authorised!', severity: 'error' }));
      return;
    }
    setFormData({
      type: 'dtc',
      code: '',
      isSingleUse: true,
    });
    setOpenCreate(true);
  };

  const createAccessCode = async () => {
    if (!formData.type) {
      dispatch(setToast({ show: true, message: 'Please select a type.', severity: 'error' }));
      return;
    }
    try {
      dispatch(setLoader(true));
      const payload = {
        type: formData.type,
        isSingleUse: formData.isSingleUse,
      };
      if (formData.code.trim()) {
        payload.code = formData.code.trim();
      }
      const res = await hit('/admin/access-codes', 'post', payload);
      if (res.err) {
        dispatch(setToast({ show: true, message: res.msg || 'Failed to create access code.', severity: 'error' }));
        return;
      }
      dispatch(setToast({ show: true, message: 'Access code created successfully.', severity: 'success' }));
      setOpenCreate(false);
      fetchAccessCodes();
    } catch (err) {
      dispatch(setToast({ show: true, message: 'Failed to create access code.', severity: 'error' }));
    } finally {
      dispatch(setLoader(false));
    }
  };

  const filteredCodes = applySortFilter(accessCodes, getComparator(order, orderBy), filterName);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredCodes.length) : 0;
  const isNotFound = !filteredCodes.length && !!filterName;

  return (
    <>
      <Helmet>
        <title> Access Codes | SuperSenses UI </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" gutterBottom>
            Access Codes
          </Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openCreateDialog}>
            Generate Access Code
          </Button>
        </Stack>

        <Card>
          <UserListToolbar
            numSelected={0}
            filterName={filterName}
            onFilterName={handleFilterByName}
            showInput
            headerText="Access Codes"
            placeholder="Search by code or type..."
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={accessCodes.length}
                  numSelected={0}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredCodes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const code = row?.code || '—';
                      const type = row?.type || '—';
                      const isSingleUse = row?.isSingleUse;
                      const isUsed = row?.isUsed;
                      const createdAt = row?.createdAt || row?.created_at;
                      const id = row?._id || row?.id || '—';

                      return (
                        <TableRow hover key={id || index} tabIndex={-1} role="checkbox">
                          <TableCell padding="checkbox">{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell component="th" scope="row" padding="none">
                            <Typography>{code}</Typography>
                          </TableCell>
                          <TableCell align="left">{type}</TableCell>
                          <TableCell align="left">{isSingleUse ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="left">{isUsed ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="left">
                            {createdAt ? moment(createdAt).format('MM/DD/YYYY hh:mm a') : '—'}
                          </TableCell>
                          <TableCell align="left">{id}</TableCell>
                        </TableRow>
                      );
                    })}

                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={7} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                        <Typography variant="body2">No access codes found.</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredCodes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Access Code</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="access-code-type-label">Type</InputLabel>
              <Select
                labelId="access-code-type-label"
                value={formData.type}
                label="Type"
                onChange={(event) => {
                  setFormData((prev) => ({ ...prev, type: event.target.value }));
                }}
              >
                <MenuItem value="dtc">dtc</MenuItem>
                <MenuItem value="vip">vip</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Custom Code (optional)"
              value={formData.code}
              onChange={(event) => {
                setFormData((prev) => ({ ...prev, code: event.target.value }));
              }}
              placeholder="Leave empty to auto-generate"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isSingleUse}
                  onChange={(event) => {
                    setFormData((prev) => ({ ...prev, isSingleUse: event.target.checked }));
                  }}
                />
              }
              label="Single use"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={createAccessCode}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
